import { useContext, useEffect, useRef, useMemo, startTransition } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import createApp from "@shopify/app-bridge";
import { NavigationMenu, AppLink } from "@shopify/app-bridge/actions";
import { AppBridgeConfigContext } from "./AppBridgeProvider";
import { toRouterPath } from "../utils/appPaths";

/**
 * Shopify admin sidebar via App Bridge NavigationMenu (actions API).
 * Menu is created once per link set — not recreated on every route change.
 */
export default function NavigationMenuComponent({ navigationLinks, matcher }) {
  const location = useLocation();
  const navigate = useNavigate();
  const config = useContext(AppBridgeConfigContext);
  const navigationMenuRef = useRef(null);
  const appRef = useRef(null);
  const appLinksRef = useRef([]);
  const linksRef = useRef([]);
  const matchFunctionRef = useRef(matcher);

  const links = useMemo(
    () => (Array.isArray(navigationLinks) ? navigationLinks : []),
    [navigationLinks],
  );

  const matchFunction = matcher || ((link, currentLocation) => {
    const dest = link.destination || "/";
    return (
      currentLocation.pathname === dest ||
      (dest !== "/" && currentLocation.pathname.startsWith(dest))
    );
  });

  matchFunctionRef.current = matchFunction;
  linksRef.current = links;

  const linksKey = useMemo(
    () => links.map((l) => `${l.label}:${l.destination}`).join("|"),
    [links],
  );

  useEffect(() => {
    if (!config?.apiKey || !config?.host) {
      return undefined;
    }

    const appBridgeConfig = {
      apiKey: config.apiKey,
      host: config.host,
    };

    try {
      if (!appRef.current) {
        appRef.current = createApp(appBridgeConfig);
      }

      const app = appRef.current;

      appLinksRef.current = linksRef.current.map((link) =>
        AppLink.create(app, {
          label: link.label,
          destination: link.destination,
        }),
      );

      if (navigationMenuRef.current) {
        try {
          navigationMenuRef.current.unsubscribe();
        } catch {
          /* noop */
        }
      }

      navigationMenuRef.current = NavigationMenu.create(app, {
        items: appLinksRef.current,
      });
    } catch (error) {
      console.error("[NavigationMenu] init failed:", error);
    }

    return () => {
      if (navigationMenuRef.current) {
        try {
          navigationMenuRef.current.unsubscribe();
        } catch {
          /* noop */
        }
        navigationMenuRef.current = null;
      }
    };
  }, [config, linksKey]);

  useEffect(() => {
    if (!navigationMenuRef.current || !appLinksRef.current.length) {
      return;
    }
    const activeLink = appLinksRef.current.find((link, index) =>
      matchFunctionRef.current(linksRef.current[index], location),
    );

    try {
      navigationMenuRef.current.set({ active: activeLink || undefined });
    } catch {
      /* noop */
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onPopState = () => {
      const routerPath = toRouterPath(window.location.pathname);
      const search = window.location.search || "";
      const current = location.pathname + location.search;
      const next = routerPath + search;
      if (next !== current) {
        startTransition(() => {
          navigate({ pathname: routerPath, search }, { replace: true });
        });
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [navigate, location.pathname, location.search]);

  return null;
}
